import { TFunction } from 'i18next';
import * as yup from 'yup';

import { translations } from 'locales/i18n';
import { euCountries } from 'utils/Countries';

export const registerValidationSchema = (t: TFunction<'translation', undefined>) => ({
  contactData: yup.object().shape({
    firstName: yup.string().required(t(translations.validation.required())),
    lastName: yup.string().required(t(translations.validation.required())),
    email: yup
      .string()
      .email(t(translations.validation.email()))
      .required(t(translations.validation.required())),
    phone: yup.string().required(t(translations.validation.required())),
    position: yup.string().required(t(translations.validation.required()))
  }),
  companyData: yup.object().shape({
    name: yup.string().required(t(translations.validation.required())),
    street: yup.string().required(t(translations.validation.required())),
    zip: yup
      .string()
      .max(5, t(translations.validation.max(), { max: 5 }))
      .required(t(translations.validation.required())),
    city: yup.string().required(t(translations.validation.required())),
    country: yup.string().required(t(translations.validation.required())),
    phone: yup.string(),
    fax: yup.string(),
    homepage: yup
      .string()
      .url(t(translations.validation.homepage()))
      .required(t(translations.validation.required())),
    email: yup
      .string()
      .email(t(translations.validation.email()))
      .required(t(translations.validation.required())),
    logo: yup.mixed()
  }),
  description: yup.string().required(),
  courses: yup.array().min(1, t(translations.validation.min(), { min: 1 })),
  useSameAddress: yup.boolean().required(t(translations.validation.required())),
  invoiceData: yup.object().when('$useSameAddress', {
    is: false,
    then: yup.object().shape(
      {
        taxId: yup
          .string()
          .when(['$country', 'taxNumber', 'taxId'], {
            is: (country, taxNumber, taxId) => country === 'Germany' && !taxNumber && !taxId,
            then: yup
              .string()
              .max(32, t(translations.validation.max(), { max: 32 }))
              .required(t(translations.validation.taxIdOrTaxNumber()))
              .matches(/^[a-zA-Z0-9]{8,15}$/, t(translations.validation.taxIdFormat()))
              .nullable(),
            otherwise: yup
              .string()
              .max(32, t(translations.validation.max(), { max: 32 }))
              .nullable()
          })
          .when('$country', {
            is: country => euCountries.includes(country) && country !== 'Germany',
            then: yup
              .string()
              .max(32, t(translations.validation.max(), { max: 32 }))
              .required(t(translations.validation.taxIdEuCountry()))
              .matches(/^[a-zA-Z0-9]{8,15}$/, t(translations.validation.taxIdFormat()))
              .nullable(),
            otherwise: yup.string().max(32, t(translations.validation.max())).nullable()
          }),
        taxNumber: yup.string().when(['$country', 'taxId', 'taxNumber'], {
          is: (country, taxId, taxNumber) => country === 'Germany' && !taxNumber && !taxId,
          then: yup
            .string()
            .max(32, t(translations.validation.max(), { max: 32 }))
            .matches(new RegExp('^[0-9]{2,3}/[0-9]{3,4}/[0-9]{3,4}[0-9]$'), {
              message: t(translations.validation.taxNumberFormat()),
              excludeEmptyString: true
            })
            .required(t(translations.validation.taxIdOrTaxNumber()))
            .nullable(),
          otherwise: yup
            .string()
            .max(32, t(translations.validation.max(), { max: 32 }))
            .matches(new RegExp('^[0-9]{2,3}/[0-9]{3,4}/[0-9]{3,4}[0-9]$'), {
              message: t(translations.validation.taxNumberFormat()),
              excludeEmptyString: true
            })
            .nullable()
        }),
        invoiceName: yup.string().required(t(translations.validation.required())),
        invoiceStreet: yup.string().required(t(translations.validation.required())),
        invoiceZip: yup
          .string()
          .max(5, t(translations.validation.max(), { max: 5 }))
          .required(t(translations.validation.required())),
        invoiceCity: yup.string().required(t(translations.validation.required())),
        invoiceCountry: yup.string().required(t(translations.validation.required())),
        invoiceEmail: yup.string().email(t(translations.validation.email())).required()
      },
      [
        ['taxId', 'taxId'],
        ['taxId', 'taxNumber'],
        ['taxNumber', 'taxId'],
        ['taxNumber', 'taxNumber']
      ]
    ),
    otherwise: yup.object().shape(
      {
        taxId: yup
          .string()
          .when(['$country', 'taxNumber', 'taxId'], {
            is: (country, taxNumber, taxId) => country === 'Germany' && !taxNumber && !taxId,
            then: yup
              .string()
              .max(32, t(translations.validation.max(), { max: 32 }))
              .required(t(translations.validation.taxIdOrTaxNumber()))
              .matches(/^[a-zA-Z0-9]{8,15}$/, t(translations.validation.taxIdFormat()))
              .nullable(),
            otherwise: yup.string().max(32, t(translations.validation.max())).nullable()
          })
          .when('$country', {
            is: country => euCountries.includes(country) && country !== 'Germany',
            then: yup
              .string()
              .max(32, t(translations.validation.max(), { max: 32 }))
              .required(t(translations.validation.taxIdEuCountry()))
              .matches(/^[a-zA-Z0-9]{8,15}$/, t(translations.validation.taxIdFormat()))
              .nullable(),
            otherwise: yup.string().max(32, t(translations.validation.max())).nullable()
          }),
        taxNumber: yup.string().when(['$country', 'taxId', 'taxNumber'], {
          is: (country, taxId, taxNumber) => country === 'Germany' && !taxNumber && !taxId,
          then: yup
            .string()
            .max(32, t(translations.validation.max(), { max: 32 }))
            .matches(new RegExp('^[0-9]{2,3}/[0-9]{3,4}/[0-9]{3,4}[0-9]$'), {
              message: t(translations.validation.taxNumberFormat()),
              excludeEmptyString: true
            })
            .required(t(translations.validation.taxIdOrTaxNumber()))
            .nullable(),
          otherwise: yup
            .string()
            .max(32, t(translations.validation.max(), { max: 32 }))
            .matches(new RegExp('^[0-9]{2,3}/[0-9]{3,4}/[0-9]{3,4}[0-9]$'), {
              message: t(translations.validation.taxNumberFormat()),
              excludeEmptyString: true
            })
            .nullable()
        }),
        invoiceEmail: yup.string().email(t(translations.validation.email())).required().nullable()
      },
      [
        ['taxId', 'taxId'],
        ['taxId', 'taxNumber'],
        ['taxNumber', 'taxId'],
        ['taxNumber', 'taxNumber']
      ]
    )
  })
});
